<template>
  <div class="load-simulation-data">
    <accordion-panel
      id="load-simulation-data__accordion"
      :collapsed.sync="collapsed"
    >
      <template #title>
        <label class="m-0">
          {{ $t('engineering.load-simulation.point-table') }}
        </label>
      </template>
      <b-row class="mb-3">
        <b-col>
          <b-form-group
            class="mb-0"
            label-class="font-weight-bold"
            :label="`${$t('fields.engineering.load-simulation.time-interval')}*`"
          >
            <s-number-input
              id="load-simulation-data-interval"
              class="load-simulation-data__interval text-right"
              v-model="$v.timeIntervalBetweenDataMinutes.$model"
              :numeric-options="getNumericPrecision('timeIntervalBetweenDataMinutes')"
              :disabled="loading || onlyErrorsFilter"
              @blur-native-proxy="$v.timeIntervalBetweenDataMinutes.$touch()"
              @input="onChangeInterval"
            />
            <b-form-invalid-feedback
              class="d-block"
              v-if="$v.timeIntervalBetweenDataMinutes.$error"
            >
              {{ getMA086('timeIntervalBetweenDataMinutes') }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col class="d-flex justify-content-end align-items-end">
          <b-checkbox
            v-show="showErrorsFilter"
            v-model="onlyErrorsFilter"
            :disabled="loading"
            @input="onChangeErrorsFilter"
          >
            {{ $t('fields.engineering.load-simulation.only-errors', { amount: errorsAmount }) }}
          </b-checkbox>
        </b-col>
      </b-row>
      <div class="load-simulation-data__points mb-3">
        <s-table
          class="load-simulation-data__points-table mb-0"
          sticky-header
          head-variant="var(--color-table-header-bg)"
          small
          ref="pointsTable"
          primary-key="id"
          :fields="fields"
          :items="items"
        >
          <template #empty>
            <div class="text-center py-4 no-internal-print">
              <b-button
                v-if="!onlyErrorsFilter"
                size="sm"
                variant="outline-secondary"
                :disabled="loading"
                @click="addPoint()"
              >
                <span class="fa fa-plus"></span>
                {{ $t('engineering.load-simulation.add-first-point') }}
              </b-button>
            </div>
          </template>
          <template #head(menu)>
            <b-dropdown
              class="load-simulation-data__menu no-internal-print"
              variant="link"
              offset="-15"
              right
              no-caret
              :class="hideMenuClass"
              :disabled="loading"
            >
              <template slot="button-content">
                <i
                  aria-hidden="true"
                  class="fas fa-ellipsis-v text-gray"
                />
              </template>
              <b-dropdown-item
                v-if="showDeleteAll"
                link-class="text-danger"
                :disabled="loading"
                @click="deleteAll"
              >
                <span class="fa fa-trash"></span> {{ $t('actions.delete-all') }}
              </b-dropdown-item>
              <b-dropdown-item
                v-if="showDeleteErrors"
                link-class="text-danger"
                :disabled="loading"
                @click="deleteErrors"
              >
                <span class="fa fa-trash"></span> {{ $t('actions.delete-errors') }}
              </b-dropdown-item>
            </b-dropdown>
          </template>
          <template #cell(point)="{ item }">
            {{ item.point }}
          </template>
          <template #cell(dateTime)="{ item }">
            {{ item.dateTime | dateTimeFilter({ format: dateMask, fallbackValue: '--' }) }}
          </template>
          <template #cell(environmentTemperature)="{ item, index }">
            <s-number-input
              class="text-right"
              v-model="item.environmentTemperature"
              :id="item ? getHtmlId('environmentTemperature', item.id) : ''"
              :numeric-options="getNumericPrecision('environmentTemperature')"
              :validation="getVPointByItem(item)['environmentTemperature']"
              :invalid="$errorProxy(item, 'environmentTemperature')"
              :disabled="loading"
              @blur-native-proxy="onEnvironmentTemperatureBlur(item)"
              @input="onUpdatePoint(item, index)"
            />
          </template>
          <template #cell(loading)="{ item, index }">
            <s-number-input
              class="text-right"
              v-model="item.loading"
              :numeric-options="getNumericPrecision('loading')"
              :validation="getVPointByItem(item)['loading']"
              :invalid="$errorProxy(item, 'loading')"
              :disabled="loading"
              @blur-native-proxy="onPointsFieldBlur('loading', item)"
              @input="onUpdatePoint(item, index)"
            />
          </template>
          <template #cell(situation)="{ item }">
            <span
              :class="getSituationClass(item)"
              v-b-tooltip.html.leftbottom="getPointMessageFeedback(item)"
            ></span>
          </template>
          <template #cell(menu)="{ item, index }">
            <div class="load-simulation-data__points-table-td-menu-btns">
              <b-button
                v-show="!onlyErrorsFilter"
                size="sm"
                variant="outline-secondary"
                :disabled="loading"
                @click="addPoint(item)"
              >
                <span class="fa fa-plus"></span>
              </b-button>
              <b-button
                size="sm"
                variant="outline-danger"
                :disabled="loading"
                @click="removePoint(item, index)"
              >
                <span class="fa fa-trash"></span>
              </b-button>
            </div>
          </template>
        </s-table>
      </div>
      <grid-paginator
        v-model="currentPage"
        class="modal-paginator"
        :total-rows="totalItems"
        :per-page="perPage"
        @input="paging()"
      />
    </accordion-panel>
  </div>
</template>
<script>
import { required, between } from 'vuelidate/lib/validators';
import store from '@/store';
import eventBus from '@/services/globalEventBus';
import {
  formatter as getFormattedDate,
  joinISODateAndTimeString,
  plus,
} from '@/services/dateTime/datetimeService';
import { formats } from '@/constants/localizer';
import { getUniqueId } from '@/services/createId';
import AccordionPanel from '@/components/AccordionPanel';
import GridPaginator from '@/components/lists/Paginator';
import dataFields from '@/domain/data/installation/engineering/loadSimulation/dataFields';
import events from '@/domain/events/engineering';

const DEFAULT_POINTS_PER_PAGE = 100;
const DEFAULT_INTERVAL_TO_CHECK_FOCUS = 50;
const DEFAULT_INTERVAL_DELAY_MS = 500;

export default {
  name: 'LoadSimulationDataPointsTable',
  props: {
    status: {
      type: Object,
      required: true,
    },
    initialDate: {
      type: String,
      required: true,
    },
    initialTime: {
      type: String,
      required: true,
    },
    pointsList: {
      type: Array,
      default: () => ([]),
    },
    interval: {
      type: Number,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    AccordionPanel,
    GridPaginator,
  },
  data: vm => ({
    getFormattedDate,
    dateMask: formats.DATETIME_SHORT,
    collapsed: false,
    onlyErrorsFilter: false,
    timeIntervalBetweenDataMinutes: vm.interval,
    points: vm.pointsList.map((item, index) => ({ ...item, point: index + 1 })),
    validPoints: [],
    errorPoints: [],
    errorsAmount: 0,
    lastIdCreated: null,
    checkNextFocusListener: null,
    updateAllPointsListener: null,
    onChangeIntervalListener: null,
    onUpdatePointListener: null,
    currentPage: 1,
    perPage: DEFAULT_POINTS_PER_PAGE,
  }),
  validations() {
    const $each = {
      $trackBy: 'id',
      environmentTemperature: {
        required,
        between: between(
          dataFields.environmentTemperature.minValue,
          dataFields.environmentTemperature.maxValue,
        ),
      },
      loading: {
        required,
        between: between(
          dataFields.loading.minValue,
          dataFields.loading.maxValue,
        ),
      },
    };

    return {
      timeIntervalBetweenDataMinutes: {
        required,
        between: between(
          dataFields.timeIntervalBetweenDataMinutes.minValue,
          dataFields.timeIntervalBetweenDataMinutes.maxValue,
        ),
      },
      points: {
        required,
        length: () => !!this.points.length,
        $each,
      },
    };
  },
  computed: {
    showErrorsFilter() {
      return !!this.errorsAmount || (this.errorPoints || []).length;
    },
    showDeleteAll() {
      return (this.points || []).length && !this.onlyErrorsFilter;
    },
    showDeleteErrors() {
      return this.errorsAmount > 0 && this.onlyErrorsFilter;
    },
    hideMenuClass() {
      return { invisible: !this.showDeleteAll && !this.showDeleteErrors };
    },
    fields() {
      return [
        {
          key: 'point',
          label: this.$t('fields.engineering.load-simulation.points'),
          tdClass: 'load-simulation-data__points-table-td-point',
          thClass: 'load-simulation-data__points-table-th-point',
        },
        {
          key: 'dateTime',
          label: this.$t('fields.engineering.load-simulation.date-time'),
          tdClass: 'load-simulation-data__points-table-td-date-time',
          thClass: 'load-simulation-data__points-table-th-date-time',
        },
        {
          key: 'environmentTemperature',
          label: this.$t('fields.engineering.load-simulation.environment-temperature'),
          tdClass: 'load-simulation-data__points-table-td-environment-temperature',
          thClass: 'load-simulation-data__points-table-th-environment-temperature',
        },
        {
          key: 'loading',
          label: this.$t('fields.engineering.load-simulation.loading'),
          tdClass: 'load-simulation-data__points-table-td-loading',
          thClass: 'load-simulation-data__points-table-th-loading',
        },
        {
          key: 'situation',
          label: this.$t('fields.engineering.load-simulation.situation'),
          tdClass: 'load-simulation-data__points-table-td-situation',
          thClass: 'load-simulation-data__points-table-th-situation',
        },
        {
          key: 'menu',
          label: '',
          tdClass: 'load-simulation-data__points-table-td-menu',
          thClass: 'load-simulation-data__points-table-th-menu',
        },
      ];
    },
    initialDateTime() {
      return joinISODateAndTimeString(this.initialDate, this.initialTime);
    },
    computedPoints() {
      if (this.onlyErrorsFilter) {
        return this.errorPoints;
      }

      return this.points;
    },
    items() {
      return (this.computedPoints || [])
        .slice(this.paginateFirstIndex, this.paginateLastIndex);
    },
    invalid() {
      return this.$v.$invalid;
    },
    paginateFirstIndex() {
      return (this.currentPage - 1) * this.perPage;
    },
    paginateLastIndex() {
      return (this.currentPage * this.perPage);
    },
    totalItems() {
      return (this.computedPoints || []).length;
    },
  },
  created() {
    this.initEvents();
  },
  mounted() {
    this.updateAllPoints(true, false, true);
  },
  beforeDestroy() {
    this.removeEvents();
  },
  methods: {
    setAccordionContentToTop() {
      const element = document.getElementsByClassName('suppressed-items__accordion')[0];

      if (element) {
        element.scrollTop = 0;
      }
    },
    paging() {
      this.setAccordionContentToTop();
    },
    getNumericPrecision(fieldName) {
      const precision = dataFields[fieldName].precision || 0;
      return {
        decimalPlaces: precision,
      };
    },
    getHtmlId(fieldName, id) {
      return `${fieldName}_${id}`;
    },
    getRealIndexByPage(index) {
      return ((this.currentPage - 1) * this.perPage) + index;
    },
    getIndexByPoint(point) {
      return point.point - 1;
    },
    getVPointByItem(item) {
      const index = this.getIndexByPoint(item);
      return this.$v.points.$each[index] || {};
    },
    $errorProxy(item, fieldName) {
      const vPoint = this.getVPointByItem(item);
      return vPoint[fieldName] ? vPoint[fieldName].$error : false;
    },
    getMA037(fieldName, i18nPath) {
      return this.$t('messages.MA037.message', {
        0: this.$t(i18nPath),
        1: this.$n(dataFields[fieldName].minValue, {
          minimumFractionDigits: dataFields[fieldName].precision,
          maximumFractionDigits: dataFields[fieldName].precision,
        }),
        2: this.$n(dataFields[fieldName].maxValue, {
          minimumFractionDigits: dataFields[fieldName].precision,
          maximumFractionDigits: dataFields[fieldName].precision,
        }),
      });
    },
    getMA086(fieldName) {
      return this.$t('messages.MA086.message', {
        minValue: this.$n(dataFields[fieldName].minValue),
        maxValue: this.$n(dataFields[fieldName].maxValue),
      });
    },
    getPointMessageFeedback(item) {
      const point = this.getVPointByItem(item);
      let message = '';
      if (point?.environmentTemperature?.$anyError) {
        message += `<div>${this.getMA037(
          'environmentTemperature',
          'fields.engineering.load-simulation.environment-temperature',
        )}</div>`;
      }
      if (point?.loading?.$anyError) {
        message += `<div>${this.getMA037(
          'loading',
          'fields.engineering.load-simulation.loading',
        )}</div>`;
      }
      return message;
    },
    getSituationClass(item) {
      const vItem = this.getVPointByItem(item);
      if (!vItem.$dirty && !vItem.$anyError) return '';
      return vItem.$anyError ? 'fa fa-times text-danger' : 'fa fa-check text-success';
    },
    getNewPoint() {
      this.lastIdCreated = getUniqueId();
      return {
        id: this.lastIdCreated,
        point: null,
        dateTime: null,
        environmentTemperature: null,
        loading: null,
      };
    },
    setFocus(id) {
      const htmlId = this.getHtmlId('environmentTemperature', id);
      this.$nextTick(() => {
        const inputElement = document.getElementById(htmlId);
        if (!inputElement) return;
        inputElement.focus();
      });
    },
    addErrorPointToList(item) {
      this.errorPoints.push({ ...item });
    },
    removeErrorsPoints() {
      this.points = this.points.filter((item, index) => !this.$v.points.$each[index].$invalid);
    },
    updatePromise(callTouch = false, removeAllErrors = false, setErrorsList = false) {
      return new Promise((resolve) => {
        clearTimeout(this.updateAllPointsListener);

        this.updateAllPointsListener = setTimeout(() => {
          this.updateAllPoints(callTouch, removeAllErrors, setErrorsList);
          resolve();
        }, 0);
      });
    },
    updateAllPoints(callTouch = false, removeAllErrors = false, setErrorsList = false) {
      const { timeIntervalBetweenDataMinutes } = this;
      let nextDatetime = this.initialDateTime;
      this.validPoints = [];
      this.errorsAmount = 0;

      if (removeAllErrors) {
        this.removeErrorsPoints();
      }

      if (removeAllErrors || setErrorsList) this.$set(this, 'errorPoints', []);

      for (let index = 0; index < this.points.length; index += 1) {
        const item = this.points[index];
        item.point = index + 1;
        item.dateTime = nextDatetime;

        if (callTouch) {
          this.$v.points.$each[index].$touch();
        }

        if (this.$v.points.$each[index].environmentTemperature.$error) this.errorsAmount += 1;
        if (this.$v.points.$each[index].loading.$error) this.errorsAmount += 1;
        if (!this.$v.points.$each[index].$invalid) this.validPoints.push(item);

        if (setErrorsList) {
          if (this.$v.points.$each[index].$anyError) this.addErrorPointToList(item);
        }

        nextDatetime = timeIntervalBetweenDataMinutes
          ? plus(nextDatetime, { minutes: timeIntervalBetweenDataMinutes })
          : null;
      }

      this.$nextTick(() => {
        this.storeDispatch();
        this.fixOnlyErrorsFilterStatus();
      });
    },
    fixOnlyErrorsFilterStatus() {
      if (!(this.errorPoints || []).length) {
        this.onlyErrorsFilter = false;
      }
    },
    fixTableFocus(index) {
      const listLength = (this.items || []).length;
      const currentIndex = index >= listLength ? (listLength - 1) : index;

      if (currentIndex < 0) return;

      const focusId = this.items[currentIndex].id;
      this.setFocus(focusId);
    },
    checkIfNextFocusIsLoadingInput(item) {
      const realIndex = this.getIndexByPoint(item);
      const { id } = this.points[realIndex];

      if (id !== this.lastIdCreated) return;

      clearTimeout(this.checkNextFocusListener);

      this.checkNextFocusListener = setTimeout(() => {
        const htmlId = this.getHtmlId('loading', id);
        const inputElement = document.getElementById(htmlId);

        if (inputElement !== document.activeElement) {
          const vItem = this.getVPointByItem(item);
          vItem.$touch();
        }
      }, DEFAULT_INTERVAL_TO_CHECK_FOCUS);
    },
    pointsLimitReached() {
      this.$pushAlert({
        code: 'ME072',
        messageParams: {
          0: this.status.informedMaximumPointsLimit,
        },
      });
    },
    addPoint(point) {
      if ((this.points || []).length === this.status?.informedMaximumPointsLimit) {
        this.pointsLimitReached();
        return;
      }

      const index = point ? this.getIndexByPoint(point) : 0;
      const position = index + 1;
      const newPoint = this.getNewPoint();
      this.points.splice(position, 0, newPoint);
      this.updatePromise()
        .then(() => {
          if (position % this.perPage === 0) {
            this.currentPage += 1;
          }
          this.setFocus(newPoint.id);
        });
    },
    fixCurrentPageOnRemovePoint(tableIndex) {
      if (tableIndex === 0 && !this.items[1] && this.currentPage > 1) {
        this.currentPage -= 1;
      }
    },
    fixErrorPointsPoint(tableIndex) {
      this.errorPoints.forEach((item, errorIndex) => {
        if (errorIndex >= tableIndex) {
          this.errorPoints[errorIndex].point = item.point - 1;
        }
      });
    },
    removePoint(point, tableIndex) {
      const index = this.getIndexByPoint(point);
      const pageIndex = this.getRealIndexByPage(tableIndex);

      if (this.points[index].id === point.id) {
        this.fixCurrentPageOnRemovePoint(tableIndex);

        this.points.splice(index, 1);
        this.errorPoints.splice(pageIndex, 1);

        this.fixErrorPointsPoint(tableIndex);
      }

      this.updatePromise(true, false, false)
        .then(() => {
          this.fixTableFocus(tableIndex);
        });
    },
    deleteCallback() {
      this.points = [];
      this.validPoints = [];
      this.errorPoints = [];
      this.errorsAmount = 0;
      this.cleanAll();
      this.$nextTick(() => {
        this.fixOnlyErrorsFilterStatus();
        this.$pushAlert({ code: 'MS034' });
      });
    },
    deleteAll() {
      this.$confirmMessage({ code: 'MC011' })
        .accepted(() => this.deleteCallback());
    },
    deleteErrorsCallback() {
      this.updatePromise(true, true)
        .then(() => {
          this.$nextTick(() => {
            if (!(this.points || []).length) this.cleanAll();
            this.$pushAlert({ code: 'MS034' });
          });
        });
    },
    deleteErrors() {
      this.$confirmMessage({ code: 'MC055' })
        .accepted(() => this.deleteErrorsCallback());
    },
    onChangeInterval(value) {
      clearTimeout(this.onChangeIntervalListener);

      this.onChangeIntervalListener = setTimeout(() => {
        store.dispatch('setSimulationDataInterval', value);
        store.dispatch(
          'setSimulationDataIsIntervalInvalid',
          this.$v.timeIntervalBetweenDataMinutes.$invalid,
        );
        this.updatePromise(true, false, true);
      }, DEFAULT_INTERVAL_DELAY_MS);
    },
    onChangeErrorsFilter() {
      this.updatePromise(true, false, true);
    },
    onEnvironmentTemperatureBlur(item) {
      this.onPointsFieldBlur('environmentTemperature', item);
      this.checkIfNextFocusIsLoadingInput(item);
      this.updateAllPoints();
    },
    onUpdatePoint(point) {
      const index = this.getIndexByPoint(point);

      if (!point?.id || !this.points[index]?.id) return;

      if (this.points[index].id === point.id) {
        Object.assign(this.points[index], point);
      }

      clearTimeout(this.onUpdatePointListener);

      this.onUpdatePointListener = setTimeout(() => {
        if (!this.onlyErrorsFilter) {
          this.updatePromise(false, false, true);
          return;
        }

        this.updatePromise(true, false, false);
      }, DEFAULT_INTERVAL_DELAY_MS);
    },
    onPointsFieldBlur(fieldName, item) {
      const vPoint = this.getVPointByItem(item);
      if (vPoint[fieldName]) vPoint[fieldName].$touch();
      this.updateAllPoints();
    },
    cleanupAndParseFloat(value) {
      const valueFormatted = this.$n(value, { maximumFractionDigits: 3, useGrouping: false });
      const valueReplaced = (valueFormatted || '').replace(',', '.');
      return parseFloat(valueReplaced) || undefined;
    },
    onImportSimulation(importedPoints) {
      const { points } = importedPoints;
      this.$set(this, 'points', []);
      this.$set(this, 'errorPoints', []);

      const mappedPoints = points.map(({ environmentTemperature, loading }) => {
        const point = this.getNewPoint();
        point.environmentTemperature = this.cleanupAndParseFloat(environmentTemperature);
        point.loading = this.cleanupAndParseFloat(loading);
        return point;
      });

      this.$set(this, 'points', mappedPoints);
      this.onlyErrorsFilter = false;
      this.currentPage = 1;
      this.updatePromise(true, false, true);
    },
    storeDispatch() {
      store.dispatch('setSimulationDataPoints', this.points);
      store.dispatch('setSimulationDataIsInvalid', !!this.errorsAmount);
      store.dispatch('setSimulationDataValidPoints', this.validPoints);
      store.dispatch('setSimulationDataIsEmpty', !(this.points || []).length);
      store.dispatch('setSimulationDataInterval', this.interval);
    },
    cleanAll() {
      this.storeDispatch();
      store.dispatch('setSimulationDataImportData', {});
      eventBus.emit(events.onSimulationPointsListEmpty);
    },
    initEvents() {
      eventBus.on(events.onImportSimulation, this.onImportSimulation, this);
    },
    removeEvents() {
      eventBus.off(events.onImportSimulation, this.onImportSimulation, this);
    },
  },
};
</script>
<style scoped lang="sass">
.load-simulation-data
  border: 1px solid var(--color-table-header-bg)
  border-radius: 4px

  &__interval
    max-width: 11em

  &__points
    border: 1px solid var(--color-table-header-bg)
    border-radius: 4px

::v-deep
  .b-table-sticky-header
    overflow-y: scroll

  .load-simulation-data
    &__points-table-td-situation
      font-size: 1.3rem

    &__points-table-td-environment-temperature,
    &__points-table-td-loading
      text-align: center

    &__points-table-td-environment-temperature > input,
    &__points-table-td-loading > input
      display: inline-block
      max-width: 100px

    &__points-table-td-menu-btns
      display: inline-block
      width: 74px
      @media print
        display: none !important
</style>
